import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { 
  Button, 
  TextField, 
  FormControl,
  InputLabel,
  Select,
  List, 
  ListItem, 
  ListItemText, 
  Chip, 
  MenuItem, 
  InputAdornment, 
  Box, 
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Paper,
  Stack,
  Badge,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonIcon from '@mui/icons-material/Person';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import NoteIcon from '@mui/icons-material/Note';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BranchSelector from './BranchSelector';

const getInsuranceTypeColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'auto': return '#2196f3';
    case 'home': return '#ff9800';
    case 'business': return '#4caf50';
    case 'life': return '#f44336';
    case 'travel': return '#9c27b0';
    default: return '#757575';
  }
};

const statusChipColor = (status) => {
  switch (status) {
    case 'Contracted':  return 'success';
    case 'Declined':    return 'error';
    case 'No Response': return 'warning';
    case 'Contacted':   return 'info';
    case 'New':
    default:            return 'default';
  }
};
const statusChipProps = (value) => {
  switch (value) {
    case 'Contracted': return { color: 'success', icon: <CheckCircleIcon /> };
    case 'Declined': return { color: 'error', icon: <PersonIcon /> };
    case 'No Response': return { color: 'warning', icon: <PersonIcon /> };
    case 'Contacted': return { color: 'info', icon: <CheckCircleIcon /> };
    case 'New':
    default: return { color: 'default', icon: <PersonIcon /> };
  }
};

const columns = [
  { 
    field: 'visitorName', 
    headerName: 'Name', 
    flex: 1.2,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: '#6366f1', fontSize: '0.875rem' }}>
          {params.value?.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      </Box>
    )
  },
  { 
    field: 'phoneNumber', 
    headerName: 'Phone', 
    flex: 1,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {params.value}
      </Typography>
    )
  },
  { 
    field: 'email', 
    headerName: 'Email', 
    flex: 1.5,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 500 }}>
        {params.value}
      </Typography>
    )
  },
  {
    field: 'insuranceTypes',
    headerName: 'Interests',
    flex: 1.8,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {params.value?.slice(0, 2).map((type, index) => (
          <Chip 
            key={index} 
            label={type.type} 
            size="small"
            sx={{ 
              backgroundColor: `${getInsuranceTypeColor(type.type)}15`,
              color: getInsuranceTypeColor(type.type),
              fontWeight: 600,
              fontSize: '0.7rem'
            }} 
          />
        ))}
        {params.value?.length > 2 && (
          <Chip 
            label={`+${params.value.length - 2}`} 
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        )}
      </Box>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1.8,
    sortable: false,
    renderCell: (params) => {
      const interest = params.row?.insuranceTypes || [];
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {interest.map((it, idx) => (
            <Tooltip key={idx} title={`$it.type}: ${it.status}`}>
        <Chip 
          label={`${it.type} (${it.status})`} 
          color={statusChipColor(it.status)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
      ))}
      </Box>
    );
  }
  },
  {
    field: 'createdAt',
    headerName: 'Submitted',
    flex: 1,
    renderCell: (params) => (
      <Typography variant="body2" color="text.secondary">
        {new Date(params.value).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })}
      </Typography>
    )
  }
];

export default function AdminDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const { isSuperAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [pdfSnackbar, setPdfSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setPdfSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => {
    setPdfSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();

        if (selectedBranch) params.append('branchId', selectedBranch);
        if (selectedEvent)  params.append('eventId', selectedEvent);

        const res = await axios.get(`/api/submissions?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const submissionsData = Array.isArray(res.data) ? res.data : res.data.submissions || [];

        const withIds = submissionsData.map((entry) => ({
          ...entry,
          id: entry._id
        }));

        setSubmissions(withIds);
        setFiltered(withIds);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [selectedBranch, selectedEvent]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/events', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEvents(res.data);
      } catch (err) {
        console.error('Failed to fetch events:', err);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const filteredData = submissions.filter((item) => {
      const matchesSearch =
        item.visitorName?.toLowerCase().includes(search.toLowerCase()) ||
        item.phoneNumber?.includes(search) ||
        item.email?.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === 'All' ||
  // overall for early stages
  (['New','Contacted'].includes(statusFilter) && item.status === statusFilter) ||
  // per-interest for final outcomes
  (['Contracted','Declined','No Response'].includes(statusFilter) &&
    item.insuranceTypes?.some(it => it.status === statusFilter)); 

      return matchesSearch && matchesStatus;
    });
    setFiltered(filteredData);
  }, [search, statusFilter, submissions]);

  const newCount = submissions.filter(s => s.status === 'New').length;
  const contactedCount = submissions.filter(s => s.status === 'Contacted').length;

  const handleDownloadPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/submissions/${selectedSubmission.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${selectedSubmission.visitorName}-submission.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      showSnackbar('Failed to download PDF', 'error');
    }
  };
  
  const handleListPdf = async () => {
  if (!filtered.length) {
    showSnackbar('No submissions to include for the current view', 'warning');
    return;
  }
  setListLoading(true);
  try {
    const token = localStorage.getItem('token');
    const ids = filtered.map(r => r.id); // export exactly what’s visible
    const res = await axios.post(
      '/api/submissions/list-pdf',
      { ids },
      { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
    );

    const today = new Date().toISOString().slice(0,10);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `submission-list-${today}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    showSnackbar(`Exported ${ids.length} row(s)`);
  } catch (err) {
    console.error(err);
    showSnackbar('List PDF download failed', 'error');
  } finally {
    setListLoading(false);
  }
};

const handleExcelExport = async () => {
  if (!filtered.length) {
    showSnackbar('No submissions to export', 'warning');
    return;
  }
  setListLoading(true);
  try {
    const token = localStorage.getItem('token');
    const ids = filtered.map(r => r.id);
    const res = await axios.post(
      '/api/submissions/list-excel',
      { ids },
      { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
    );

    const today = new Date().toISOString().slice(0,10);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `submissions-list-${today}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    showSnackbar(`Exported ${ids.length} row(s) to Excel`);
  } catch (err) {
    console.error(err);
    showSnackbar('Excel export failed', 'error');
  } finally {
    setListLoading(false);
  }
};

  const patchLocal = (data) => {
    const updated = { ...data, id: data._id };
    setSelectedSubmission(updated);
    setSubmissions(s => s.map(x => x.id === updated.id ? updated : x));
    setFiltered(f => f.map(x => x.id === updated.id ? updated : x));
  };

  return (
    <Box sx={{ 
      p: 3,
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Compact Filters and Stats Row */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {/* Stats Pills */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${submissions.length}`} 
              color="primary"
              size="medium"
              sx={{ fontWeight: 600, px: 1 }}
              icon={<ListAltIcon />}
            />
          </Box>

          {/* Filter Controls */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              alignItems: 'center' 
            }}
          >
            <TextField
              label="Search submissions"
              placeholder="Name, phone or email"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              select
              label="Status Filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              variant="outlined"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Contacted">Contacted</MenuItem>
              <MenuItem value="Contracted">Contracted</MenuItem>
              <MenuItem value="Declined">Declined</MenuItem>
              <MenuItem value="No Response">No Response</MenuItem>
            </TextField>

            {isSuperAdmin?.() && (
              <Box sx={{ minWidth: 160 }}>
                <BranchSelector
                  selectedBranchId={selectedBranch}
                  onBranchChange={setSelectedBranch}
                  label="Filter by Branch"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}

            <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Event</InputLabel>
              <Select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
                label="Filter by Event"
              >
                <MenuItem value="">All Events</MenuItem>
                {events.map(event => (
                  <MenuItem key={event._id} value={event._id}>
                    {event.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
  variant="contained"
  startIcon={<PictureAsPdfIcon />}
  onClick={handleListPdf}
  disabled={listLoading || filtered.length === 0}
  sx={{ fontWeight: 600 }}
>
  {listLoading ? 'Preparing…' : 'PDF'}
</Button>
<Button
  variant="contained"
  startIcon={<InsertDriveFileIcon />}
  onClick={handleExcelExport}
  disabled={listLoading || filtered.length === 0}
  sx={{ fontWeight: 600 }}
>
  {listLoading ? 'Preparing…' : 'Excel'}
</Button>
          </Box>
        </Box>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={filtered}
          columns={columns}
          pageSize={10}
          disableSelectionOnClick
          onRowClick={(params) => {
            if (selectedSubmission?.id === params.row.id) {
              setSelectedSubmission(null);
            } else {
              setSelectedSubmission(params.row);
            }
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              fontWeight: 600
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(99, 102, 241, 0.04)',
              cursor: 'pointer'
            }
          }}
        />
      </Paper>

      {/* Details */}
      <AnimatePresence>
        {selectedSubmission && (
          <motion.div
            key={selectedSubmission.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <Paper sx={{ 
              mt: 3, 
              borderRadius: 3, 
              overflow: 'hidden',
              border: '1px solid #e5e7eb'
            }}>
              {/* Header */}
              <Box sx={{ 
                p: 3, 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e5e7eb 100%)',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: '#6366f1', 
                      width: 48, 
                      height: 48,
                      fontSize: '1.2rem'
                    }}>
                      {selectedSubmission.visitorName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        {selectedSubmission.visitorName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Submission Details
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton 
                    onClick={() => setSelectedSubmission(null)}
                    sx={{ 
                      bgcolor: 'white',
                      boxShadow: 1,
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Contact Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Contact Information
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Tooltip title="Call">
                      <Button 
                        variant="outlined" 
                        startIcon={<PhoneIcon />}
                        href={`tel:${selectedSubmission.phoneNumber}`}
                        sx={{ borderRadius: 2 }}
                      >
                        {selectedSubmission.phoneNumber}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Send Email">
                      <Button 
                        variant="outlined" 
                        startIcon={<EmailIcon />}
                        href={`mailto:${selectedSubmission.email}?subject=Insurance Inquiry`}
                        sx={{ borderRadius: 2 }}
                      >
                        {selectedSubmission.email}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                      <Button 
                        variant="outlined" 
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadPdf}
                        sx={{ borderRadius: 2 }}
                      >
                        PDF
                      </Button>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2">
                    <strong>Contact Methods:</strong>{' '}
                    {selectedSubmission.contactMethods && selectedSubmission.contactMethods.length > 0
                      ? selectedSubmission.contactMethods.map((method, i) => (
                          <Chip
                            key={i}
                            label={method.charAt(0).toUpperCase() + method.slice(1)}
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))
                      : 'None'}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />

                {/* Insurance Interests */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Insurance Interests
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedSubmission.insuranceTypes?.map((type, idx) => (
                      <Paper key={idx} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        {/* Chips */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Chip 
                            label={type.type} 
                            sx={{ 
                              backgroundColor: `${getInsuranceTypeColor(type.type)}15`,
                              color: getInsuranceTypeColor(type.type),
                              fontWeight: 600
                            }}
                          />
                          <Chip 
                            label={type.isInsured ? 'Currently Insured' : 'Not Insured'} 
                            color={type.isInsured ? 'success' : 'warning'}
                            size="small"
                          />
                          {type.wantsQuote && (
                            <Chip label="Wants Quote" color="info" size="small" />
                          )}
                        </Box>

                        {/* Renewal & carrier */}
                        {type.renewalDate && (
                          <Typography variant="body2" color="text.secondary">
                            Renewal Date: {new Date(type.renewalDate).toLocaleDateString()}
                          </Typography>
                        )}
                        {type.carrier && (
                          <Typography variant="body2" color="text.secondary">
                            Carrier: {type.carrier}
                          </Typography>
                        )}

                        {/* Per-interest finalization buttons (allowed while overall not final) */}
                        {!['Contracted','Declined','No Response'].includes(type.status) && (
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            {['Contracted', 'Declined', 'No Response'].map(option => (
                              <Button
                                key={option}
                                variant="outlined"
                                size="small"
                                color={
                                  option === 'Contracted' ? 'success' 
                                  : option === 'Declined'   ? 'error' 
                                                            : 'warning'
                                }
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const { data } = await axios.patch(
                                      `/api/submissions/${selectedSubmission.id}/insurance/${encodeURIComponent(type.type)}/status`,
                                      { status: option },
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    const updated = { ...data, id: data._id };
            setSelectedSubmission(updated);
            setSubmissions(s => s.map(x => x.id === updated.id ? updated : x));
            setFiltered(f => f.map(x => x.id === updated.id ? updated : x));
          } catch (err) {
            console.error(err);
          }
        }}
        sx={{ borderRadius: 1, px: 2 }}
      >
        {option}
      </Button>
    ))}
  </Box>
)}

                        {/* Show per-interest status if set */}
                        {['Contracted','Declined','No Response','Contacted'].includes(type.status) && (
                          <Chip
                            label={type.status}
                            size="small"
                            color={
                              type.status === 'Contracted' ? 'success' 
                              : type.status === 'Declined'   ? 'error' 
                              : type.status === 'No Response' ? 'warning'
                              : 'info'
                            }
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Additional Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Additional Information
                  </Typography>
                  <Stack spacing={1}>
                    <Typography>
                      <strong>Current Broker:</strong> {selectedSubmission.currentBroker || 'Not specified'}
                    </Typography>
                    <Typography>
                      <strong>Comments:</strong> {selectedSubmission.comments || 'None specified'}
                    </Typography>
                    <Typography>
                      <strong>Consent Given:</strong> 
                      <Chip 
                        label={selectedSubmission.consentGiven ? 'Yes' : 'No'} 
                        color={selectedSubmission.consentGiven ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Stack>
                </Box>

                {/* Signature */}
                {selectedSubmission.signature && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Digital Signature
                    </Typography>
                    <Paper sx={{ p: 2, display: 'inline-block', borderRadius: 2 }}>
                      <img
                        src={selectedSubmission.signature}
                        alt="Digital signature"
                        style={{ maxWidth: '300px', maxHeight: '150px' }}
                      />
                    </Paper>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Overall actions */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* New -> Contacted */}
                  {selectedSubmission.status === 'New' && (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      startIcon={<CheckCircleIcon />}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const { data } = await axios.patch(
                            `/api/submissions/${selectedSubmission.id}`,
                            { status: 'Contacted' },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          patchLocal(data);
                        } catch (err) {
                          console.error(err);
                          showSnackbar('Failed to mark as Contacted', 'error');
                        }
                      }}
                      sx={{ borderRadius: 2, px: 4 }}
                    >
                      Mark as Contacted
                    </Button>
                  )}

                  {/* If overall final, show chip */}
                  {['Contracted', 'Declined', 'No Response'].includes(selectedSubmission.status) && (
                    <Chip
                      label={selectedSubmission.status}
                      size="medium"
                      color={
                        selectedSubmission.status === 'Contracted' ? 'success' :
                        selectedSubmission.status === 'Declined'   ? 'error'   :
                        'warning'
                      }
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                {/* Notes */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <NoteIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Internal Notes
                      <Badge badgeContent={selectedSubmission.notes?.length || 0} color="primary" sx={{ ml: 1 }} />
                    </Typography>
                  </Box>
                  
                  {selectedSubmission.notes?.length > 0 && (
                    <Paper sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                      <List dense>
                        {selectedSubmission.notes.map((note, idx) => (
                          <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {note.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              By {note.author} • {new Date(note.timestamp).toLocaleString()}
                            </Typography>
                            {idx < selectedSubmission.notes.length - 1 && <Divider sx={{ width: '100%', mt: 1 }} />}
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                  
                  <TextField
                    label="Add internal note"
                    multiline
                    rows={3}
                    fullWidth
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddCommentIcon />}
                    onClick={async () => {
                      if (!noteText.trim()) return;
                      try {
                        const token = localStorage.getItem('token');
                        const notePayload = {
                          author: localStorage.getItem('username'),
                          text: noteText
                        };
                        const { data } = await axios.patch(
                          `/api/submissions/${selectedSubmission.id}`,
                          { note: notePayload },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        patchLocal(data);
                        setNoteText('');
                      } catch (err) {
                        console.error('Failed to add note', err);
                        showSnackbar('Failed to add note', 'error');
                      }
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Note
                  </Button>
                </Box>
              </CardContent>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={pdfSnackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={pdfSnackbar.severity}>
          {pdfSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
